import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { useState, useEffect } from "react";
import { locationScopeKey } from "@/lib/location-scope";

const fetchAPI = async (path: string, options?: RequestInit) => {
  const res = await fetch(`/api${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...options?.headers },
    credentials: "include",
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export function useNotifications(options?: {
  enabled?: boolean;
  limit?: number;
  offset?: number;
  unreadOnly?: boolean;
  eventType?: string;
}) {
  const { user, currentWorkspace } = useAuth();
  const queryClient = useQueryClient();

  const limit = options?.limit ?? 50;
  const offset = options?.offset ?? 0;
  const unreadOnly = options?.unreadOnly ?? false;
  const eventType = options?.eventType;
  const listEnabled = !!user && (options?.enabled !== false);

  const { data: unreadData } = useQuery({
    queryKey: ["/api/notifications/unread-count", ...locationScopeKey(currentWorkspace?.id, user?.id)],
    queryFn: () => fetchAPI("/notifications/unread-count"),
    enabled: !!user,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (unreadOnly) params.set("unreadOnly", "true");
  if (eventType) params.set("eventType", eventType);

  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ["/api/notifications", ...locationScopeKey(currentWorkspace?.id, user?.id, undefined, { limit, offset, unreadOnly, eventType })],
    queryFn: () => fetchAPI(`/notifications?${params}`),
    enabled: listEnabled,
    staleTime: 15_000,
  });

  const notifications = (notificationsData?.notifications ?? []) as any[];
  const unreadCount: number = unreadData?.count ?? 0;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
  };

  const markReadMutation = useMutation({
    mutationFn: (id: string) => fetchAPI(`/notifications/${id}/read`, { method: "POST" }),
    onSuccess: invalidate,
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => fetchAPI("/notifications/read-all", { method: "POST" }),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => fetchAPI(`/notifications/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  const deleteAllMutation = useMutation({
    mutationFn: () => fetchAPI("/notifications", { method: "DELETE" }),
    onSuccess: invalidate,
  });

  return {
    notifications,
    unreadCount,
    isLoading,
    markRead: markReadMutation.mutate,
    markAllRead: markAllReadMutation.mutate,
    deleteNotification: deleteMutation.mutate,
    deleteAll: deleteAllMutation.mutate,
    isDeleting: deleteAllMutation.isPending,
  };
}

// ─── Push notifications hook ─────────────────────────────────────────────────

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return new Uint8Array(Array.from(rawData).map((c) => c.charCodeAt(0)));
}

export function usePushNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    setIsSupported("serviceWorker" in navigator && "PushManager" in window);
    if (typeof Notification !== "undefined") {
      setPermission(Notification.permission);
    }
  }, []);

  const subscribe = async (): Promise<boolean> => {
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return false;

      const registration = await navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" });
      await navigator.serviceWorker.ready;

      const res = await fetch("/api/notifications/vapid-public-key", { credentials: "include" });
      if (!res.ok) return false;
      const { key } = await res.json();
      if (!key) return false;

      const existing = await registration.pushManager.getSubscription();
      const subscription = existing ?? await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });

      if (!subscription) return false;
      const sub = subscription.toJSON();

      await fetch("/api/notifications/push-subscribe", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ endpoint: sub.endpoint, keys: sub.keys }),
      });

      return true;
    } catch {
      return false;
    }
  };

  return { isSupported, permission, subscribe };
}
